﻿import { useState } from 'react';
import { Box, CircularProgress, Stack, Typography, Alert } from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import { useGetMetricsQuery } from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { localDate, addDays } from '@/utils/helpers';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { RefetchButton } from '@/components/admin/RefetchButton';
import { MetricsDateRangePicker } from '@/components/metrics/MetricsDateRangePicker';
import { MetricsUserFilter } from '@/components/metrics/MetricsUserFilter';
import { MetricsActivitySection } from '@/components/metrics/MetricsActivitySection';
import { MetricsUsersSection } from '@/components/metrics/MetricsUsersSection';
import { MetricsAssignmentsSection } from '@/components/metrics/MetricsAssignmentsSection';
import { MetricsCommunicationSection } from '@/components/metrics/MetricsCommunicationSection';
import { MetricsUsagePatternsSection } from '@/components/metrics/MetricsUsagePatternsSection';
import { MetricsScheduleSection } from '@/components/metrics/MetricsScheduleSection';
import { MetricsScreenshotsSection } from '@/components/metrics/MetricsScreenshotsSection';
const AdminMetricsPage = () => {
    const { LL } = useI18nContext();
    const [range, setRange] = useState({
        from: addDays(localDate(), -90),
        to: addDays(localDate(), 30),
    });
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const queryParams = {
        ...range,
        ...(selectedUser !== null ? { resourceId: selectedUser } : {}),
    };
    const { data, isLoading, isError, refetch, isFetching } = useGetMetricsQuery(queryParams);
    if (isLoading) {
        return (
            <Stack
                sx={{ justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}
            >
                <CircularProgress size={56} />
                <Typography
                    variant="h6"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {LL.common.loading()}
                </Typography>
            </Stack>
        );
    }
    if (isError || !data) {
        return (
            <>
                <AdminPageHeader
                    icon={<BarChartIcon />}
                    title={LL.metricsPage.title()}
                    subtitle={LL.metricsPage.subtitle()}
                />
                <Box sx={{ px: 3 }}>
                    <Alert severity="error">{LL.errors.failedToLoad()}</Alert>
                </Box>
            </>
        );
    }
    return (
        <>
            <AdminPageHeader
                icon={<BarChartIcon />}
                title={LL.metricsPage.title()}
                subtitle={LL.metricsPage.subtitle()}
                refetchButton={<RefetchButton onRefetch={refetch} isRefetching={isFetching} />}
            />
            <Box sx={{ px: 3, pb: 6 }}>
                <MetricsDateRangePicker value={range} onChange={setRange} />
                <MetricsUserFilter
                    resources={data.resourceList}
                    selectedId={selectedUser}
                    onChange={setSelectedUser}
                />

                <MetricsActivitySection data={data} />
                <MetricsUsagePatternsSection data={data} />
                <MetricsScreenshotsSection data={data} />
                <MetricsUsersSection data={data} />
                <MetricsAssignmentsSection data={data} />
                <MetricsScheduleSection data={data} />
                <MetricsCommunicationSection data={data} />
            </Box>
        </>
    );
};
export default AdminMetricsPage;
